export const GOAL_KINDS = ["feed", "reels", "stories"] as const;
export type GoalKind = (typeof GOAL_KINDS)[number];

export type DistributionBasis = "calendar" | "weekdays";

export interface GoalCounts {
  [kind: string]: number;
}

export interface GoalDay {
  date: string;
  counts: GoalCounts;
}

export interface GoalWeek {
  index: number;
  startDate: string;
  endDate: string;
  counts: GoalCounts;
  days: GoalDay[];
}

export interface DistributionSettings {
  mode: "auto" | "manual";
  basis: DistributionBasis;
  includeSaturday: boolean;
}

const KIND_LABEL: Record<string, string> = {
  feed: "Feed",
  reels: "Reels",
  stories: "Stories",
};

export function goalKindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind;
}

export function emptyCounts(kinds: readonly string[] = GOAL_KINDS): GoalCounts {
  const out: GoalCounts = {};
  for (const kind of kinds) out[kind] = 0;
  return out;
}

export function normalizeCounts(raw: unknown, kinds: readonly string[] = GOAL_KINDS): GoalCounts {
  const source = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const keys = new Set<string>([...kinds, ...Object.keys(source)]);
  const out: GoalCounts = {};
  for (const key of keys) {
    if (!/^[a-z][a-z0-9_]{0,31}$/.test(key)) continue;
    const n = Number(source[key]);
    out[key] = Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
  }
  for (const kind of kinds) {
    if (out[kind] == null) out[kind] = 0;
  }
  return out;
}

export function addCounts(a: GoalCounts, b: GoalCounts): GoalCounts {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const out: GoalCounts = {};
  for (const key of keys) out[key] = (a[key] ?? 0) + (b[key] ?? 0);
  return out;
}

export function countsKinds(counts: GoalCounts): string[] {
  const extras = Object.keys(counts).filter((key) => !GOAL_KINDS.includes(key as GoalKind));
  return [...GOAL_KINDS, ...extras];
}

export function distributeInteger(total: number, slots: number): number[] {
  if (slots <= 0) return [];
  const safe = Math.max(0, Math.floor(total));
  const base = Math.floor(safe / slots);
  const remainder = safe % slots;
  return Array.from({ length: slots }, (_, index) => base + (index < remainder ? 1 : 0));
}

export interface MonthDay {
  date: string;
  weekday: number;
}

export function daysOfMonth(month: string): MonthDay[] {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return [];
  const year = Number(match[1]);
  const monthIndex = Number(match[2]);
  const count = new Date(Date.UTC(year, monthIndex, 0)).getUTCDate();
  const days: MonthDay[] = [];
  for (let day = 1; day <= count; day++) {
    const utc = new Date(Date.UTC(year, monthIndex - 1, day));
    days.push({
      date: `${month}-${String(day).padStart(2, "0")}`,
      weekday: utc.getUTCDay() || 7,
    });
  }
  return days;
}

export function isEligibleDay(
  weekday: number,
  basis: DistributionBasis,
  includeSaturday: boolean,
): boolean {
  if (basis === "calendar") return true;
  if (weekday >= 1 && weekday <= 5) return true;
  return includeSaturday && weekday === 6;
}

export function weekLabel(week: Pick<GoalWeek, "index" | "startDate" | "endDate">): string {
  const fmt = (iso: string) => {
    const [, m, d] = iso.split("-");
    return `${d}/${m}`;
  };
  return `Sem ${week.index} · ${fmt(week.startDate)} a ${fmt(week.endDate)}`;
}

function groupWeeks(days: Array<MonthDay & { counts: GoalCounts }>, kinds: string[]): GoalWeek[] {
  const weeks: GoalWeek[] = [];
  let current: GoalWeek | null = null;
  for (const day of days) {
    if (!current || day.weekday === 1) {
      current = {
        index: weeks.length + 1,
        startDate: day.date,
        endDate: day.date,
        counts: emptyCounts(kinds),
        days: [],
      };
      weeks.push(current);
    }
    current.endDate = day.date;
    current.days.push({ date: day.date, counts: day.counts });
  }
  return weeks.map((week) => ({
    ...week,
    counts: week.days.reduce((acc, day) => addCounts(acc, day.counts), emptyCounts(kinds)),
  }));
}

export function buildAutoPlan(input: {
  month: string;
  counts: GoalCounts;
  basis: DistributionBasis;
  includeSaturday: boolean;
}): GoalWeek[] {
  const counts = normalizeCounts(input.counts);
  const kinds = countsKinds(counts);
  const days = daysOfMonth(input.month);
  const eligible = days
    .map((day, index) => (isEligibleDay(day.weekday, input.basis, input.includeSaturday) ? index : -1))
    .filter((index) => index >= 0);
  const shares: Record<string, number[]> = {};
  for (const kind of kinds) {
    shares[kind] = distributeInteger(counts[kind] ?? 0, eligible.length);
  }
  const planned = days.map((day, index) => {
    const slot = eligible.indexOf(index);
    const dayCounts = emptyCounts(kinds);
    if (slot >= 0) {
      for (const kind of kinds) dayCounts[kind] = shares[kind][slot] ?? 0;
    }
    return { ...day, counts: dayCounts };
  });
  return groupWeeks(planned, kinds);
}

export function sumWeekCounts(weeks: GoalWeek[]): GoalCounts {
  return weeks.reduce((acc, week) => addCounts(acc, week.counts), {});
}

export function sumDayCounts(days: GoalDay[]): GoalCounts {
  return days.reduce((acc, day) => addCounts(acc, day.counts), {});
}

export function validateGoalPlan(monthCounts: GoalCounts, weeks: GoalWeek[]): {
  ok: boolean;
  messages: string[];
} {
  const messages: string[] = [];
  const weekSum = sumWeekCounts(weeks);
  const kinds = new Set([...countsKinds(monthCounts), ...Object.keys(weekSum)]);
  for (const kind of kinds) {
    const diff = (monthCounts[kind] ?? 0) - (weekSum[kind] ?? 0);
    if (diff > 0) messages.push(`Faltam ${diff} ${goalKindLabel(kind)} para fechar o mês`);
    if (diff < 0) messages.push(`Excedeu em ${Math.abs(diff)} ${goalKindLabel(kind)}`);
  }
  for (const week of weeks) {
    const daySum = sumDayCounts(week.days);
    const weekKinds = new Set([...Object.keys(week.counts), ...Object.keys(daySum)]);
    for (const kind of weekKinds) {
      const diff = (week.counts[kind] ?? 0) - (daySum[kind] ?? 0);
      if (diff > 0) {
        messages.push(`Sem ${week.index}: faltam ${diff} ${goalKindLabel(kind)} para fechar a semana`);
      }
      if (diff < 0) {
        messages.push(`Sem ${week.index}: excedeu em ${Math.abs(diff)} ${goalKindLabel(kind)}`);
      }
    }
  }
  return { ok: messages.length === 0, messages };
}

export function rebalanceToMonth(
  weeks: GoalWeek[],
  monthCounts: GoalCounts,
  basis: DistributionBasis,
  includeSaturday: boolean,
): GoalWeek[] {
  const counts = normalizeCounts(monthCounts);
  const kinds = countsKinds(counts);
  const flat = weeks.flatMap((week) =>
    week.days.map((day) => ({
      date: day.date,
      weekday: new Date(`${day.date}T12:00:00.000Z`).getUTCDay() || 7,
      counts: { ...emptyCounts(kinds), ...day.counts },
    })),
  );
  for (const kind of kinds) {
    const current = flat.reduce((sum, day) => sum + (day.counts[kind] ?? 0), 0);
    let delta = (counts[kind] ?? 0) - current;
    const eligible = flat
      .map((day, index) => (isEligibleDay(day.weekday, basis, includeSaturday) ? index : -1))
      .filter((index) => index >= 0);
    const targets = eligible.length > 0 ? eligible : flat.map((_, index) => index);
    if (delta > 0) {
      let cursor = 0;
      while (delta > 0 && targets.length > 0) {
        const index = targets[cursor % targets.length];
        flat[index].counts[kind] = (flat[index].counts[kind] ?? 0) + 1;
        delta -= 1;
        cursor += 1;
      }
    } else if (delta < 0) {
      let left = -delta;
      for (const index of targets) {
        if (left <= 0) break;
        const have = flat[index].counts[kind] ?? 0;
        const take = Math.min(have, left);
        flat[index].counts[kind] = have - take;
        left -= take;
      }
    }
  }
  const byDate = new Map(flat.map((day) => [day.date, day.counts]));
  return weeks.map((week) => {
    const days = week.days.map((day) => ({
      date: day.date,
      counts: { ...emptyCounts(kinds), ...(byDate.get(day.date) ?? day.counts) },
    }));
    return {
      ...week,
      days,
      counts: days.reduce((acc, day) => addCounts(acc, day.counts), emptyCounts(kinds)),
    };
  });
}

export function previousMonthKey(month: string): string {
  const [year, monthNumber] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year, monthNumber - 2, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function shiftMonth(month: string, delta: number): string {
  const [year, monthNumber] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year, monthNumber - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}
